import { z } from 'zod';
import { PopulatedUserSchema } from '../user/user.schema';
import * as recordingModule from '../process-step-recording/process-step-recording.schema';
/**
 * Survey Status Enum
 * Represents the completion status of a survey.
 *
 * `dismissed` is the second TERMINAL state (ADR 0022): the recipient answered
 * the Variant Check with "Ich mache diesen Schritt nicht", which ends the
 * survey for them without any answers. It is never pending (nothing is owed,
 * so it leaves My Tasks and carries no due date), never completed (there is
 * no rating, frequency or duration to read) and never capacity-eligible — a
 * dismissed member leaves the team-extrapolation denominator instead. Only a
 * re-survey returns the row to `pending`.
 */ export const SurveyStatusSchema = z.enum([
    'pending',
    'completed',
    'dismissed'
]);
/**
 * Survey Rating Schema
 * Contains a star rating and an optional free-text comment
 */ export const SurveyRatingSchema = z.object({
    stars: z.number().min(1).max(5),
    comment: z.string().optional()
});
/**
 * Survey Frequency Schema
 * Contains a frequency value and an optional free-text comment.
 *
 * `timesPerPeriod` refines the "mehrmals" options (multiple-times-daily,
 * several-times-a-week) with an explicit occurrence count. When absent, the
 * capacity calculation falls back to the legacy default baked into
 * FREQUENCY_VALUES — so existing surveys need no migration.
 */ export const SurveyFrequencySchema = z.object({
    value: z.string(),
    comment: z.string().optional(),
    timesPerPeriod: z.number().int().positive().optional()
});
/**
 * Survey Duration Schema
 * Contains a duration value (in minutes) and an optional free-text comment
 */ export const SurveyDurationSchema = z.object({
    value: z.number().positive(),
    comment: z.string().optional()
});
/**
 * Variant Check Answer (ADR 0022, plan 029)
 * A co-performer's one-time answer about the step they were surveyed on:
 * `same` = "Ja, genau so", `different` = "Ja, aber anders" (which carries a
 * mandatory description) and `not_performed` = "Ich mache diesen Schritt
 * nicht".
 *
 * All three are ONE vocabulary because all three are stored the same way: as
 * the `verdict` of a `StepVariantResponse` document. The decline additionally
 * dismisses the survey row (D10), but that status is a materialized
 * consequence of the answer, never its storage — so no reader has to
 * reconstruct an answer from two places.
 */ export const VariantCheckAnswerSchema = z.enum([
    'same',
    'different',
    'not_performed'
]);
/**
 * Survey Schema
 * Complete schema for a survey entity
 * Represents feedback from a user on a process step recording
 *
 * Note: `stepRecording` references SurveyStepRecordingSchema via z.lazy. The survey ↔ recording
 * module cycle is intentional — both schemas reference each other at the entity level. We use
 * a namespace import combined with `z.lazy` so the cross-reference is deferred until parse time
 * (i.e., after both modules have finished initializing). A bare `require()` here would throw
 * `ReferenceError` in the ESM build that Vite resolves for the frontend.
 */ export const SurveySchema = z.object({
    _id: z.string(),
    stepRecording: z.union([
        z.string(),
        z.lazy(()=>recordingModule.SurveyStepRecordingSchema)
    ]),
    user: z.union([
        z.string(),
        PopulatedUserSchema
    ]),
    status: SurveyStatusSchema,
    rating: SurveyRatingSchema.optional(),
    frequency: SurveyFrequencySchema.optional(),
    duration: SurveyDurationSchema.optional(),
    completedAt: z.string().optional(),
    dismissedAt: z.string().optional(),
    deletedAt: z.string().optional(),
    dueDate: z.number().optional(),
    createdAt: z.string(),
    updatedAt: z.string()
});
/**
 * A survey plus whether it still owes THIS recipient a Variant Check verdict
 * (ADR 0022). Scope — the tenant toggle, and the step's author never being
 * asked — is only half of it: the answer is a `StepVariantResponse` document
 * (plan 029) and it is immutable, so the flag also goes false once one exists
 * for this recipient at this row's generation. A flag that reported scope alone
 * would offer a question the submit could only refuse.
 *
 * Server-computed and per recipient, which is why it rides on the response
 * rather than the stored row. Never re-derive it client-side.
 */ export const SurveyWithVariantCheckSchema = SurveySchema.extend({
    awaitsVariantVerdict: z.boolean()
});

//# sourceMappingURL=survey.schema.js.map